import React, {
  useCallback,
  createContext,
  useState,
  useMemo,
  useRef,
  ReactElement
} from 'react';
import { fabric } from 'fabric';
import 'src/fabric-overrides';
import { enableCanvasPixelRatio } from '@pages/MediaViewer/AssetViewer/Canvas/utils/canvasPixelRatio';

interface IFabricContext {
  canvas: fabric.Canvas | undefined;
  mouseOverObjectRef: { current: boolean };
  isMouseDownRef: { current: boolean };
  objectToCreateRef: { current: fabric.Object | undefined };
  activeObjectRef: { current: fabric.Object | undefined };
  draftPinPlacedRef: { current: boolean };
  cursorHintRef: { current: HTMLDivElement | null };
  initCanvas: (canvas: HTMLCanvasElement | null) => void;
  getObjects: () => fabric.Object[];
  clearCanvas: () => void;
}

export const FabricContext = createContext<IFabricContext>(undefined as any);

type FabricContextProviderProps = {
  children: ReactElement | ReactElement[];
};

export function FabricContextProvider({
  children
}: FabricContextProviderProps) {
  const [canvas, setCanvas] = useState<fabric.Canvas>();
  const mouseOverObjectRef = useRef<boolean>(false);
  const isMouseDownRef = useRef<boolean>(false);
  const objectToCreateRef = useRef<fabric.Object>();
  const activeObjectRef = useRef<fabric.Object>();
  const draftPinPlacedRef = useRef<boolean>(false);
  const cursorHintRef = useRef<HTMLDivElement | null>(null);

  const initCanvas = useCallback((template) => {
    const canvas = new fabric.Canvas(template, {
      selectionColor: 'transparent',
      selectionLineWidth: 0,
      centeredScaling: true,
      targetFindTolerance: 5,
      preserveObjectStacking: true,
      perPixelTargetFind: true,
      // Enabled below, within a pixel budget
      enableRetinaScaling: false,
      width: 300,
      height: 300,
      allowTouchScrolling: true
    });
    enableCanvasPixelRatio(canvas);
    canvas.renderAll();
    setCanvas(canvas);
  }, []);

  const getObjects = useCallback(() => {
    if (!canvas) return [];
    return canvas.getObjects().filter((item) => item.type !== 'highlight');
  }, [canvas]);

  const clearCanvas = useCallback(() => {
    if (!canvas) return;
    mouseOverObjectRef.current = false;
    isMouseDownRef.current = false;
    objectToCreateRef.current = undefined;
    canvas.clear();
    canvas.requestRenderAll();
  }, [canvas]);

  const providerValue = useMemo(() => {
    return {
      canvas,
      initCanvas,
      getObjects,
      clearCanvas,
      mouseOverObjectRef,
      isMouseDownRef,
      activeObjectRef,
      objectToCreateRef,
      draftPinPlacedRef,
      cursorHintRef
    };
  }, [canvas, initCanvas, getObjects, clearCanvas]);

  return (
    <FabricContext.Provider value={providerValue}>
      {children}
    </FabricContext.Provider>
  );
}

export default FabricContextProvider;
